// Formatos de Colombia: `$ 1.950.000`, `27,4 %`, `3 oct 2026`.
import type { Moneda } from '../api/tipos';

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function agruparMiles(entero: string): string {
  return entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

export function numero(v: number | null | undefined, decimales = 0): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '·';
  const negativo = v < 0;
  const fijo = Math.abs(v).toFixed(decimales);
  const [ent, dec] = fijo.split('.');
  return `${negativo ? '-' : ''}${agruparMiles(ent)}${dec ? `,${dec}` : ''}`;
}

export function dinero(v: number | null | undefined, moneda: Moneda = 'COP'): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '·';
  if (moneda === 'USD') {
    const s = numero(Math.abs(v), 2);
    return `${v < 0 ? '-' : ''}USD ${s}`;
  }
  const s = numero(Math.round(Math.abs(v)));
  return `${v < 0 ? '-' : ''}$ ${s}`;
}

/** Valor en su moneda original (multimoneda, §13.9): COP con pesos; otras con su código y dos decimales. */
export function dineroEn(v: number | null | undefined, moneda: string | null | undefined): string {
  if (!moneda || moneda === 'COP' || moneda === 'USD') return dinero(v, (moneda as Moneda) || 'COP');
  if (v === null || v === undefined || Number.isNaN(v)) return '·';
  return `${v < 0 ? '-' : ''}${moneda} ${numero(Math.abs(v), 2)}`;
}

/** Valor abreviado para ejes y tarjetas: `$ 3,2 M`, `$ 1.250 M`, `$ 850 mil`. */
export function dineroCorto(v: number | null | undefined, moneda: Moneda = 'COP'): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '·';
  const pre = moneda === 'USD' ? 'USD ' : '$ ';
  const a = Math.abs(v);
  const signo = v < 0 ? '-' : '';
  if (a >= 1e9) return `${signo}${pre}${numero(a / 1e6, 0)} M`;
  if (a >= 1e6) return `${signo}${pre}${numero(a / 1e6, a >= 1e8 ? 0 : 1)} M`;
  if (a >= 1e3) return `${signo}${pre}${numero(a / 1e3, 0)} mil`;
  return `${signo}${pre}${numero(a, 0)}`;
}

/** Fracción a porcentaje: 0.274 → `27,4 %`. */
export function pct(v: number | null | undefined, decimales = 1): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '·';
  return `${numero(v * 100, decimales)} %`;
}

/** Puntos porcentuales con signo: -0.021 → `-2,1 pp`. */
export function puntos(v: number | null | undefined, decimales = 1): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '·';
  const s = numero(Math.abs(v * 100), decimales);
  return `${v > 0 ? '+' : v < 0 ? '-' : ''}${s} pp`;
}

function aFecha(v: string | Date | null | undefined): Date | null {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(/^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T12:00:00` : v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fecha(v: string | Date | null | undefined): string {
  const d = aFecha(v);
  if (!d) return '·';
  return `${d.getDate()} ${MESES[d.getMonth()]} ${d.getFullYear()}`;
}

export function fechaHora(v: string | Date | null | undefined): string {
  const d = aFecha(v);
  if (!d) return '·';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${fecha(d)}, ${hh}:${mm}`;
}

/** Duración legible desde una fecha: `3 h`, `2 d 4 h`. */
export function transcurrido(desde: string | null | undefined, ahora = new Date()): string {
  const d = aFecha(desde);
  if (!d) return '·';
  const horas = Math.max(0, (ahora.getTime() - d.getTime()) / 36e5);
  if (horas < 1) return `${Math.max(1, Math.round(horas * 60))} min`;
  if (horas < 24) return `${Math.round(horas)} h`;
  const dias = Math.floor(horas / 24);
  const resto = Math.round(horas - dias * 24);
  return resto ? `${dias} d ${resto} h` : `${dias} d`;
}

export function horasDesde(desde: string | null | undefined, ahora = new Date()): number {
  const d = aFecha(desde);
  return d ? (ahora.getTime() - d.getTime()) / 36e5 : 0;
}

export function vencido(v: string | null | undefined, ahora = new Date()): boolean {
  const d = aFecha(v);
  return !!d && d.getTime() < ahora.getTime();
}

export function bytes(v: number | null | undefined): string {
  if (!v && v !== 0) return '·';
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${numero(v / 1024, 0)} KB`;
  return `${numero(v / 1024 / 1024, 1)} MB`;
}

/** Fecha ISO `AAAA-MM-DD` local. */
export function isoDia(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Lee un número escrito en formato colombiano (`1.950.000`, `27,4`). */
export function leerNumero(texto: string): number | null {
  const limpio = texto.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
  if (!limpio || limpio === '-' ) return null;
  const n = Number(limpio);
  return Number.isFinite(n) ? n : null;
}

export function iniciales(nombre: string): string {
  return nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
